import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { DashboardLayout } from "./components/DashboardLayout";
import { ADMIN_BASE_URL } from "./config/admin";
import NoIndex from "./components/NoIndex";

// Pages
import Landing from "./pages/Landing";
import AboutUs from "./pages/AboutUs";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import CompleteSetup from "./pages/CompleteSetup";
import Dashboard from "./pages/Dashboard";
import DNSThreats from "./pages/DNSThreats";
import FileThreats from "./pages/FileThreats";
import EmailThreats from "./pages/EmailThreats";
import AppThreats from "./pages/AppThreats";
import Users from "./pages/Users";
import Automation from "./pages/Automation";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import Referrals from "./pages/Referrals";
import MSPPortal from "./pages/MSPPortal";
import GlobalAdmin from "./pages/GlobalAdmin";
import AgentDownload from "./pages/AgentDownload";
import AdminSupport from "./pages/AdminSupport";
import TenantDetail from "./pages/TenantDetail";
import WebhooksPage from "./pages/WebhooksPage";
import SystemUsers from "./pages/SystemUsers";
import SystemAuditLogs from "./pages/SystemAuditLogs";
import CompanyManagement from "./pages/CompanyManagement";
import CompanyDetail from "./pages/CompanyDetail";
import MSPManagement from "./pages/MSPManagement";
import PromotionManagement from "./pages/PromotionManagement";
import MSPDetail from "./pages/MSPDetail";
import IntegrationsSettings from "./pages/IntegrationsSettings";
import DeviceManagement from "./pages/DeviceManagement";
import ThreatIntelligence from "./pages/ThreatIntelligence";
import CustomRules from "./pages/CustomRules";

// MSP Pages
import MSPSignup from "./pages/MSPSignup";
import MSPLogin from "./pages/MSPLogin";
import MSPDashboard from "./pages/MSPDashboard";
import MSPClients from "./pages/MSPClients";
import MSPClientDetail from "./pages/MSPClientDetail";
import MSPTeam from "./pages/MSPTeam";
import MSPEarnings from "./pages/MSPEarnings";
import MSPSettings from "./pages/MSPSettings";
import MSPSupport from "./pages/MSPSupport";
import DirectClientOnboarding from "./pages/DirectClientOnboarding";
import MSPClientOnboarding from "./pages/MSPClientOnboarding";

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return <DashboardLayout>{children}</DashboardLayout>;
};

// MSP Protected Route - no DashboardLayout, MSP pages have their own layout
const MSPProtectedRoute = ({ children }) => {
  const { token, loading, isMSP } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/msp/login" replace />;
  }
  
  if (!isMSP) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { token, user, loading, isMSP } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (token) {
    // Redirect MSP users to /msp, super_admin to /admin, regular users to /dashboard
    if (isMSP) {
      return <Navigate to="/msp" replace />;
    }
    if (user?.role === 'super_admin') {
      return <Navigate to={ADMIN_BASE_URL} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Admin Protected Route with NoIndex
const AdminProtectedRoute = ({ children }) => {
  const { token, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  // Only allow super_admin, support, and billing_admin roles
  const allowedRoles = ['super_admin', 'support', 'billing_admin'];
  if (!allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <>
      <NoIndex />
      <DashboardLayout>{children}</DashboardLayout>
    </>
  );
};

// Super Admin Only Route
const SuperAdminOnlyRoute = ({ children }) => {
  const { token, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!token || user?.role !== 'super_admin') {
    return <Navigate to={ADMIN_BASE_URL} replace />;
  }
  
  return (
    <>
      <NoIndex />
      <DashboardLayout>{children}</DashboardLayout>
    </>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/complete-setup" element={<CompleteSetup />} />
      <Route path="/onboarding" element={<DirectClientOnboarding />} />
      
      {/* MSP Public Routes */}
      <Route path="/msp/signup" element={<MSPSignup />} />
      <Route path="/msp/login" element={<MSPLogin />} />
      <Route path="/msp/verify-email" element={<VerifyEmail />} />
      
      {/* MSP Protected Routes */}
      <Route path="/msp" element={<MSPProtectedRoute><MSPDashboard /></MSPProtectedRoute>} />
      <Route path="/msp/clients" element={<MSPProtectedRoute><MSPClients /></MSPProtectedRoute>} />
      <Route path="/msp/clients/:clientId" element={<MSPProtectedRoute><MSPClientDetail /></MSPProtectedRoute>} />
      <Route path="/msp/clients/onboard" element={<MSPProtectedRoute><MSPClientOnboarding /></MSPProtectedRoute>} />
      <Route path="/msp/support" element={<MSPProtectedRoute><MSPSupport /></MSPProtectedRoute>} />
      <Route path="/msp/team" element={<MSPProtectedRoute><MSPTeam /></MSPProtectedRoute>} />
      <Route path="/msp/earnings" element={<MSPProtectedRoute><MSPEarnings /></MSPProtectedRoute>} />
      <Route path="/msp/settings" element={<MSPProtectedRoute><MSPSettings /></MSPProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dns" element={<ProtectedRoute><DNSThreats /></ProtectedRoute>} />
      <Route path="/files" element={<ProtectedRoute><FileThreats /></ProtectedRoute>} />
      <Route path="/emails" element={<ProtectedRoute><EmailThreats /></ProtectedRoute>} />
      <Route path="/apps" element={<ProtectedRoute><AppThreats /></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><DeviceManagement /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
      <Route path="/automation" element={<ProtectedRoute><Automation /></ProtectedRoute>} />
      <Route path="/webhooks" element={<ProtectedRoute><WebhooksPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/settings/integrations" element={<ProtectedRoute><IntegrationsSettings /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
      <Route path="/agent" element={<ProtectedRoute><AgentDownload /></ProtectedRoute>} />
      <Route path="/threat-intel" element={<ProtectedRoute><ThreatIntelligence /></ProtectedRoute>} />
      <Route path="/rules" element={<ProtectedRoute><CustomRules /></ProtectedRoute>} />
      
      {/* Secret Admin Routes - noindex applied */}
      <Route path={ADMIN_BASE_URL} element={<AdminProtectedRoute><GlobalAdmin section="overview" /></AdminProtectedRoute>} />
      <Route path={`${ADMIN_BASE_URL}/companies`} element={<AdminProtectedRoute><CompanyManagement /></AdminProtectedRoute>} />
      <Route path={`${ADMIN_BASE_URL}/companies/:companyId`} element={<AdminProtectedRoute><CompanyDetail /></AdminProtectedRoute>} />
      <Route path={`${ADMIN_BASE_URL}/msps`} element={<SuperAdminOnlyRoute><MSPManagement /></SuperAdminOnlyRoute>} />
      <Route path={`${ADMIN_BASE_URL}/msps/:mspId`} element={<SuperAdminOnlyRoute><MSPDetail /></SuperAdminOnlyRoute>} />
      <Route path={`${ADMIN_BASE_URL}/system-users`} element={<SuperAdminOnlyRoute><SystemUsers /></SuperAdminOnlyRoute>} />
      <Route path={`${ADMIN_BASE_URL}/audit-logs`} element={<SuperAdminOnlyRoute><SystemAuditLogs /></SuperAdminOnlyRoute>} />
      <Route path={`${ADMIN_BASE_URL}/billing`} element={<AdminProtectedRoute><GlobalAdmin section="billing" /></AdminProtectedRoute>} />
      <Route path={`${ADMIN_BASE_URL}/promotions`} element={<SuperAdminOnlyRoute><PromotionManagement /></SuperAdminOnlyRoute>} />
      <Route path={`${ADMIN_BASE_URL}/support`} element={<AdminProtectedRoute><AdminSupport /></AdminProtectedRoute>} />
      <Route path={`${ADMIN_BASE_URL}/health`} element={<SuperAdminOnlyRoute><GlobalAdmin section="health" /></SuperAdminOnlyRoute>} />
      
      {/* Old admin routes redirect to new URL */}
      <Route path="/admin/*" element={<Navigate to={ADMIN_BASE_URL} replace />} />
      
      {/* Landing Page - Public */}
      <Route path="/" element={<Landing />} />
      
      {/* About Us Page - Public */}
      <Route path="/about" element={<AboutUs />} />
      
      {/* Terms of Service Page - Public */}
      <Route path="/terms" element={<TermsOfService />} />
      
      {/* Privacy Policy Page - Public */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      
      {/* Contact Page - Public */}
      <Route path="/contact" element={<Contact />} />
      
      {/* Default redirect for unknown routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
